/* ------------------------------------------------------------------------------------------------------------------
   YARN COMPARE
   No dependencies. Stores a snapshot of each selected variant in localStorage so the comparison works across
   products and page loads without any API calls.
------------------------------------------------------------------------------------------------------------------ */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ CONFIG */

  var CONFIG = {
    storageKey: 'yarnCompare:v1',
    viewKey: 'yarnCompare:view',
    // Entries are snapshots taken at add time, so a stored entry keeps whatever shape the Liquid
    // payload had when it was saved. Bump this whenever yarn-compare-button.liquid gains or
    // changes a field; entries written under an older schema are dropped on read instead of
    // rendering with missing data that a fixed template can no longer repair.
    schema: 2,
    maxItems: 4,
    // Re-checks price and availability against /products/{handle}.js when the modal opens.
    // Set false to render purely from the stored snapshot (no network).
    refreshOnOpen: true
  };

  // Row order and labels. Labels match locales/en.default.json pdp.overview_table.
  // source: 'variant'     -> top-level field on the stored entry
  //         'variantSpec' -> entry.variantSpecs[key]  (variant metafield, product-level fallback in Liquid)
  //         'spec'        -> entry.specs[key]         (product metafield or tag)
  //         'price' / 'rating' -> custom rendering
  var ROWS = [
    { key: 'colorDescription', label: 'Color Description', source: 'variant' },
    { key: 'sku', label: 'SKU', source: 'variant' },
    { key: 'price', label: 'Price', source: 'price' },
    { key: 'rating', label: 'Rating', source: 'rating' },
    { key: 'yarn_weight', label: 'Yarn Weight', source: 'spec' },
    { key: 'fiber', label: 'Fiber', source: 'spec' },
    { key: 'length_yd', label: 'Length (yd)', source: 'variantSpec' },
    { key: 'crochet_gauge', label: 'Crochet Gauge (4in x 4in)', source: 'spec' },
    { key: 'knit_gauge', label: 'Knit Gauge (4in x 4in)', source: 'spec' },
    { key: 'care_v2', label: 'Care', source: 'spec', transform: 'pipes' }
  ];

  var STRINGS = {
    add: 'Compare Colors & Specs',
    addMore: 'Add to Compare',
    added: 'Remove From Compare',
    full: 'Comparison full, remove one to add another',
    emptyTitle: 'No colors added yet',
    emptyBody: 'Choose a color on any yarn page and select Compare Colors & Specs to add it here.',
    noValue: '\u2014',
    noValueSpoken: 'Not available',
    differs: 'Values differ',
    outOfStock: 'Out of stock',
    allMatch: 'These colors match on every attribute shown.',
    noSwatch: 'No swatch image',
    addToBag: 'Add to Bag',
    adding: 'Adding\u2026',
    soldOut: 'Sold out',
    addedToBag: 'Added to bag',
    addFailed: 'Could not add \u2014 try again',
    addMoreTitle: 'Add another color',
    addMoreBody: 'Pick a color on any yarn page and choose Add to Compare.'
  };

  /* ------------------------------------------------------------------ ANALYTICS */

  /* Pushes to the dataLayer the theme already has (GTM-WP3DS5 in theme.liquid, plus the
     configurable container in sd-digital-marketing-head.liquid), so triggers and tags are built
     in GTM without touching the theme again. A CustomEvent goes out alongside it for anything
     that is not GTM. Both are fire-and-forget: analytics must never break the feature. */
  function track(name, detail) {
    var payload = detail || {};
    try {
      window.dataLayer = window.dataLayer || [];
      var row = { event: 'yarn_compare_' + name };
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) row[k] = payload[k];
      }
      window.dataLayer.push(row);
    } catch (e) { /* no-op */ }

    try {
      document.dispatchEvent(new CustomEvent('yarn-compare:' + name, {
        bubbles: true,
        detail: payload
      }));
    } catch (e) { /* no-op */ }
  }

  function entryPayload(entry, list) {
    return {
      product_handle: entry.handle,
      product_title: entry.productTitle,
      variant_id: entry.id,
      color: entry.title,
      sku: entry.sku,
      compare_count: list.length,
      compare_products: list.map(function (e) { return e.handle; })
        .filter(function (h, i, a) { return a.indexOf(h) === i; }).length
    };
  }

  /* ------------------------------------------------------------------ STORAGE */

  /* Falls back to memory when localStorage is unavailable -- private windows with site data
     blocked, Safari's stricter modes, quota, or a browser setting.

     Without this the feature silently never works rather than merely not persisting: a failed
     write leaves nothing stored, so the next read returns an empty list, every add looks like the
     first add, the count never passes one and the modal never opens. That is indistinguishable
     from the button being broken. The in-memory copy keeps a session working; only carrying the
     comparison between page loads is lost. */
  var memoryStore = null;
  var storageOk = true;

  function storageFailed() {
    if (storageOk) {
      storageOk = false;
      console.warn('[yarn-compare] localStorage unavailable; the comparison will not persist ' +
        'between page loads in this session.');
    }
  }

  function read() {
    if (!storageOk) return memoryStore ? memoryStore.slice() : [];
    try {
      var raw = window.localStorage.getItem(CONFIG.storageKey);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter(isValidEntry) : [];
    } catch (e) {
      storageFailed();
      return memoryStore ? memoryStore.slice() : [];
    }
  }

  function write(list) {
    // Kept regardless, so a write that fails does not lose what the shopper just did.
    memoryStore = list.slice();
    try {
      window.localStorage.setItem(CONFIG.storageKey, JSON.stringify(list));
    } catch (e) {
      storageFailed();
    }
  }

  var memoryView = null;

  function readView() {
    if (memoryView) return memoryView;
    try {
      return window.localStorage.getItem(CONFIG.viewKey) === 'swatch' ? 'swatch' : 'skein';
    } catch (e) {
      return 'skein';
    }
  }

  function writeView(mode) {
    memoryView = mode;
    try {
      window.localStorage.setItem(CONFIG.viewKey, mode);
    } catch (e) {
      /* non-fatal: ui.viewMode already holds it for this page */
    }
  }

  function isValidEntry(entry) {
    return entry &&
      typeof entry === 'object' &&
      entry.id != null &&
      entry.handle &&
      entry.schema === CONFIG.schema;
  }

  function indexOfId(list, id) {
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(id)) return i;
    }
    return -1;
  }

  /* ------------------------------------------------------------------ HELPERS */

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function srOnly(text) {
    return el('span', 'YarnCompare__SrOnly', text);
  }

  // Some shops use HTML entities in shop.money_format (&euro;, &pound;). textContent would print them literally.
  function decodeEntities(str) {
    if (str.indexOf('&') === -1) return str;
    var holder = document.createElement('textarea');
    holder.innerHTML = str;
    return holder.value;
  }

  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return decodeEntities(format);

    function build(precision, thousands, decimal) {
      if (cents == null || isNaN(cents)) return '0';
      var parts = (cents / 100).toFixed(precision).split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? whole + decimal + parts[1] : whole;
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = build(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = build(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = build(0, '.', ','); break;
      case 'amount_with_space_separator': value = build(2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = build(0, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = build(2, "'", '.'); break;
      default: value = build(2, ',', '.');
    }
    return decodeEntities(format.replace(placeholder, value));
  }

  // Only used for paths this script builds itself. Product URLs from Liquid are already localized.
  function rootUrl() {
    return (window.theme && window.theme.localeRootUrl) || '';
  }

  /* ------------------------------------------------------------------ VALUES */

  function rawValue(entry, row) {
    switch (row.source) {
      case 'variant': return entry[row.key];
      case 'variantSpec': return entry.variantSpecs ? entry.variantSpecs[row.key] : '';
      case 'spec': return entry.specs ? entry.specs[row.key] : '';
      case 'price': return entry.price;
      case 'rating': return entry.rating;
      default: return '';
    }
  }

  // Normalized string used for the "only show differences" comparison.
  function comparableValue(entry, row) {
    if (row.source === 'price') {
      return String(entry.price) + '|' + String(entry.compareAtPrice || '');
    }
    if (row.source === 'rating') {
      return String(entry.rating || '');
    }
    var value = rawValue(entry, row);
    if (value == null) return '';
    return String(value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  // reviews.rating carries its own scale_max, so normalize to a 5 star display.
  var STAR_PATH = 'M10 1.5 12.6 7l6 .8-4.4 4.2 1.1 6-5.3-2.9L4.7 18l1.1-6L1.4 7.8 7.4 7z';

  function starLayer(className, filled) {
    var layer = el('span', className);
    layer.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < 5; i++) {
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 20 20');
      svg.setAttribute('focusable', 'false');
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', STAR_PATH);
      path.setAttribute('fill', filled ? 'currentColor' : 'none');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.2');
      svg.appendChild(path);
      layer.appendChild(svg);
    }
    return layer;
  }

  /* Two stacked rows of five stars -- outlines underneath, solid on top, the solid row clipped to
     the exact percentage. Rounding to whole stars hid real differences: 4.34 and 4.73 both drew
     four, while 4.73 and 4.83 drew four and five, so a 0.39 gap looked identical and a 0.10 gap
     looked like a whole star. */
  function starsNode(rating, scaleMax) {
    var scale = Number(scaleMax) || 5;
    var value = Number(rating);
    var normalized = scale === 5 ? value : (value / scale) * 5;
    if (isNaN(normalized)) normalized = 0;
    normalized = Math.max(0, Math.min(5, normalized));

    var wrap = el('span', 'YarnCompare__Stars');
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', normalized.toFixed(1) + ' out of 5 stars');

    wrap.appendChild(starLayer('YarnCompare__StarsBase', false));
    var fill = starLayer('YarnCompare__StarsFill', true);
    fill.style.width = (normalized / 5 * 100) + '%';
    wrap.appendChild(fill);
    return wrap;
  }

  function emptyCell() {
    var frag = document.createDocumentFragment();
    var dash = el('span', 'YarnCompare__Empty', STRINGS.noValue);
    dash.setAttribute('aria-hidden', 'true');
    frag.appendChild(dash);
    frag.appendChild(srOnly(STRINGS.noValueSpoken));
    return frag;
  }

  function cellContent(entry, row) {
    var frag = document.createDocumentFragment();

    if (row.source === 'price') {
      var onSale = entry.compareAtPrice && entry.compareAtPrice > entry.price;
      var price = el('span', 'YarnCompare__Price' + (onSale ? ' YarnCompare__Price--sale' : ''), formatMoney(entry.price));
      if (onSale) price.setAttribute('aria-label', 'Sale price ' + formatMoney(entry.price));
      frag.appendChild(price);
      if (onSale) {
        var was = el('span', 'YarnCompare__PriceWas', formatMoney(entry.compareAtPrice));
        was.setAttribute('aria-label', 'Regular price ' + formatMoney(entry.compareAtPrice));
        frag.appendChild(was);
      }
      return frag;
    }

    if (row.source === 'rating') {
      // The two values come from separate metafields and can be populated independently,
      // so render whichever is present rather than blanking the cell.
      if (entry.rating == null || String(entry.rating).trim() === '') return emptyCell();

      frag.appendChild(starsNode(entry.rating, entry.ratingScale));

      // The number as well as the bar. Two ratings a tenth apart are indistinguishable as stars
      // however precisely they are drawn.
      var scale = Number(entry.ratingScale) || 5;
      var shown = scale === 5 ? Number(entry.rating) : (Number(entry.rating) / scale) * 5;
      frag.appendChild(el('span', 'YarnCompare__RatingValue', shown.toFixed(2)));
      return frag;
    }

    var value = rawValue(entry, row);
    if (value == null || String(value).trim() === '') return emptyCell();

    if (row.transform === 'pipes') {
      value = String(value).split('|').map(function (part) { return part.trim(); }).filter(Boolean).join(', ');
    }
    frag.appendChild(document.createTextNode(String(value)));
    return frag;
  }

  /* ------------------------------------------------------------------ UI STATE */

  var ui = {
    root: null, tab: null, count: null, tabLabel: null,
    modal: null, dialog: null, body: null, summary: null,
    tools: null, diff: null, announce: null, view: null,
    lastFocused: null, isOpen: false, wasLocked: false,
    viewMode: 'skein'
  };

  function announce(message) {
    if (!ui.announce) return;
    ui.announce.textContent = '';
    window.setTimeout(function () { ui.announce.textContent = message; }, 60);
  }

  /* The tab appears at the screen edge while the shopper is looking at the button they just
     clicked, so a static element simply materialising there registers as nothing. It slides in
     the first time it appears, which peripheral vision does pick up. From the second add onward
     the modal opens at the same moment, so nothing at the edge is visible anyway -- the count
     badge carries that job instead.

     The slide is a CSS animation, so the prefers-reduced-motion block disables it and the tab
     just appears. */
  var tabWasVisible = false;

  function syncTab() {
    if (!ui.tab) return;
    var list = read();
    var visible = list.length > 0;

    if (visible && !tabWasVisible) {
      ui.tab.hidden = false;
      ui.tab.classList.add('YarnCompareTab--entering');
      window.setTimeout(function () {
        if (ui.tab) ui.tab.classList.remove('YarnCompareTab--entering');
      }, 500);
    } else {
      ui.tab.hidden = !visible;
      if (!visible) ui.tab.classList.remove('YarnCompareTab--entering');
    }
    tabWasVisible = visible;

    if (ui.count) ui.count.textContent = String(list.length);
    if (ui.tabLabel) {
      ui.tabLabel.textContent = 'Open yarn comparison, ' + list.length +
        (list.length === 1 ? ' color added' : ' colors added');
    }
  }

  function syncButtons() {
    var list = read();
    $$('[data-yarn-compare-add]').forEach(function (wrap) {
      var button = $('[data-yarn-compare-toggle]', wrap);
      var label = $('[data-yarn-compare-toggle-label]', wrap);
      if (!button) return;

      var variantId = selectedVariantId(wrap);
      var inList = variantId != null && indexOfId(list, variantId) !== -1;
      var atCapacity = !inList && list.length >= CONFIG.maxItems;

      button.setAttribute('aria-pressed', inList ? 'true' : 'false');

      // Deliberately not disabled. A disabled button cannot be clicked or focused, so it would be
      // a dead end; at capacity this opens the modal instead, where a color can be removed.
      button.disabled = false;
      button.classList.toggle('YarnCompare__AddButton--full', atCapacity);

      // Four states. The zero label reads as starting something, which is wrong once a
      // comparison already exists, so it becomes "Add to Compare" from the second color on.
      if (label) {
        if (inList) {
          label.textContent = STRINGS.added;
        } else if (atCapacity) {
          label.textContent = STRINGS.full;
        } else if (list.length > 0) {
          label.textContent = STRINGS.addMore;
        } else {
          label.textContent = STRINGS.add;
        }
      }
    });
  }

  /* --- PDP swatch marker ------------------------------------------------------------------------
     Marks the colors of THIS product that are already in the comparison.

     Swatch radios carry the option value, not a variant id, so matching is on the color name.
     That is exact for single-option products; on a color-plus-size product it would mark the
     color when any size of it is in the comparison.

     Only this product's colors are marked, so a shopper comparing four colors across four
     different yarns sees nothing here.
  --------------------------------------------------------------------------------------------- */

  // A real element, not a pseudo-element: .ColorSwatch::after is the theme's outline and hit area,
  // and .ColorSwatch::before is its tooltip (theme.scss.liquid 6044), which is visibility: hidden
  // until hover. Both slots are taken.
  function setSwatchPip(label, on) {
    var pip = label.querySelector('.YarnCompareSwatchPip');
    if (on && !pip) {
      pip = document.createElement('span');
      pip.className = 'YarnCompareSwatchPip';
      pip.setAttribute('aria-hidden', 'true');
      label.appendChild(pip);
    } else if (!on && pip) {
      pip.parentNode.removeChild(pip);
    }
  }

  var markedNames = null;

  function markSwatches() {
    var wraps = $$('[data-yarn-compare-add]');
    if (!wraps.length) return;
    var list = read();

    wraps.forEach(function (wrap) {
      var data = sourceData(wrap);
      if (!data) return;

      var names = {};
      list.forEach(function (entry) {
        if (entry.handle === data.handle) {
          names[String(entry.title || '').trim().toLowerCase()] = true;
        }
      });

      // Walking every swatch and mutating labels is the most expensive thing this script does on
      // a 52-color product, and it fires on every variant change. Skip it when the set of marked
      // colors has not actually changed, which is the case for an ordinary swatch click.
      var signature = data.handle + '|' + Object.keys(names).sort().join(',');
      if (signature === markedNames) return;
      markedNames = signature;

      var scopes = [formScope(wrap)];

      // The "view all swatches" modal renders at product-template level, outside the form.
      // Only safe to claim when this page has a single product payload.
      if (wraps.length === 1) {
        var swatchModal = document.getElementById('modal-color-options');
        if (swatchModal) scopes.push(swatchModal);
      }

      scopes.forEach(function (scope) {
        $$('.ColorSwatch__Radio', scope).forEach(function (radio) {
          var label = radio.nextElementSibling;
          if (!label || !label.classList.contains('ColorSwatch')) return;
          setSwatchPip(label, names[String(radio.value || '').trim().toLowerCase()] === true);
        });
      });
    });
  }

  /* --- Keep clear of carousel arrows -----------------------------------------------------------
     Carousel next/previous buttons sit at the right edge too, and inherit z-index 1 from
     .RoundButton (theme.scss.liquid 2219) while this tab is at 8000 -- so wherever they overlap,
     the tab swallows the click. The tab is fixed and the page scrolls, so they cross at some
     scroll position on every product page and no fixed offset avoids it.

     A product page has several carousels (gallery, swatches, related products), so a single
     offset is not enough: stepping aside can land the tab on a different arrow. Each candidate
     position is tested in turn and the first clear one wins. If every position is blocked, the
     tab fades out rather than sitting on top of a control.
  --------------------------------------------------------------------------------------------- */
  var DODGE_SELECTOR = '.flickity-prev-next-button:not([disabled]), .RoundButton';
  var DODGE_OFFSETS = [0, 96, -96, 170, -170];
  var DODGE_MARGIN = 14;
  var dodgeTimer = null;
  var dodgeNodes = null;
  var dodgeNodesAt = 0;

  // The node list is cached for a second. Re-querying and measuring ~20 .RoundButton elements on
  // every scroll frame forced a synchronous layout each time, which is enough main-thread work on
  // a mid-range phone to make a tap land during a reflow and get swallowed.
  function dodgeCandidates() {
    var now = Date.now();
    if (!dodgeNodes || now - dodgeNodesAt > 1000) {
      dodgeNodes = $$(DODGE_SELECTOR).filter(function (node) {
        return node !== ui.tab && !ui.root.contains(node);
      });
      dodgeNodesAt = now;
    }
    return dodgeNodes;
  }

  function checkDodge() {
    dodgeTimer = null;
    if (!ui.tab || ui.tab.hidden) return;

    var rects = [];
    dodgeCandidates().forEach(function (node) {
      if (!node.isConnected) { dodgeNodes = null; return; }
      var r = node.getBoundingClientRect();
      if (!r.width || !r.height) return;
      if (r.bottom < -DODGE_MARGIN || r.top > window.innerHeight + DODGE_MARGIN) return;
      rects.push(r);
    });

    // Measure the at-rest box rather than the live one, or the current offset would feed back
    // into the next test and the tab would oscillate.
    var height = ui.tab.offsetHeight;
    var width = ui.tab.offsetWidth;
    var restTop = (window.innerHeight - height) / 2;
    var left = window.innerWidth - width;

    function blocked(offset) {
      var top = restTop + offset;
      var bottom = top + height;
      if (top < 8 || bottom > window.innerHeight - 8) return true;
      return rects.some(function (r) {
        return r.bottom > top - DODGE_MARGIN &&
               r.top < bottom + DODGE_MARGIN &&
               r.right > left - DODGE_MARGIN;
      });
    }

    var chosen = null;
    for (var i = 0; i < DODGE_OFFSETS.length; i++) {
      if (!blocked(DODGE_OFFSETS[i])) { chosen = DODGE_OFFSETS[i]; break; }
    }

    if (chosen === null) {
      ui.tab.classList.add('YarnCompareTab--tucked');
    } else {
      ui.tab.classList.remove('YarnCompareTab--tucked');
      ui.tab.style.transform = 'translateY(calc(-50% + ' + chosen + 'px))';
    }
  }

  // Runs after scrolling settles rather than during it. The tab only needs to be out of the way
  // when someone is about to tap an arrow, not on every frame of a flick.
  function queueDodge() {
    window.clearTimeout(dodgeTimer);
    dodgeTimer = window.setTimeout(checkDodge, 90);
  }

  function syncAll() {
    syncTab();
    syncButtons();
    markSwatches();
    queueDodge();
    if (ui.isOpen) renderTable();
  }

  function applyView(value) {
    var mode = value === 'swatch' ? 'swatch' : 'skein';
    if (mode === ui.viewMode) return;
    ui.viewMode = mode;
    writeView(mode);
    renderTable();
    announce(mode === 'swatch' ? 'Showing color swatches.' : 'Showing skein photos.');
  }

  var syncTimer = null;
  function deferredSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(function () {
      syncButtons();
      markSwatches();
    }, 0);
  }

  /* ------------------------------------------------------------------ PDP BUTTON */

  // Walks up from the button to the nearest ancestor holding the variant id input,
  // so multiple product forms on one page stay independent.
  function formScope(node) {
    var current = node;
    while (current && current !== document.body) {
      if (current.querySelector && current.querySelector('.ProductForm__VariantId')) return current;
      current = current.parentElement;
    }
    return document;
  }

  function selectedVariantId(wrap) {
    var scope = formScope(wrap);
    var input = $('.ProductForm__VariantId', scope);
    if (input && input.value) return parseInt(input.value, 10);
    var master = $('select[name="id"] option[selected]', scope) || $('select[name="id"]', scope);
    return master && master.value ? parseInt(master.value, 10) : null;
  }

  function sourceData(wrap) {
    var script = $('[data-yarn-compare-source]', wrap);
    if (!script) {
      console.warn('[yarn-compare] No data payload found on this product form.');
      return null;
    }
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[yarn-compare] Variant payload is not valid JSON:', e.message);
      return null;
    }
  }

  function buildEntry(wrap, variantId) {
    var data = sourceData(wrap);
    if (!data) return null;
    var variant = data.variants[String(variantId)];
    if (!variant) return null;

    return {
      schema: CONFIG.schema,
      id: variant.id,
      handle: data.handle,
      productTitle: data.title,
      url: variant.url || data.url,
      title: variant.title,
      sku: variant.sku,
      price: variant.price,
      compareAtPrice: variant.compareAtPrice,
      available: variant.available,
      image: variant.image,
      swatch: variant.swatch || null,
      colorDescription: variant.colorDescription,
      variantSpecs: variant.variantSpecs || {},
      rating: data.rating,
      ratingScale: data.ratingScale || 5,
      specs: data.specs,
      addedAt: Date.now()
    };
  }

  function handleToggle(button) {
    var wrap = button.closest('[data-yarn-compare-add]');
    if (!wrap) return;

    var variantId = selectedVariantId(wrap);
    if (variantId == null) {
      console.warn('[yarn-compare] No variant is currently selected.');
      return;
    }

    var list = read();
    var position = indexOfId(list, variantId);

    if (position !== -1) {
      var removed = list.splice(position, 1)[0];
      write(list);
      syncAll();
      announce(removed.productTitle + ' in ' + removed.title + ' removed from comparison. ' +
        list.length + ' remaining.');
      track('remove', entryPayload(removed, list));
      return;
    }

    if (list.length >= CONFIG.maxItems) {
      // Nothing to add, so take them straight to where they can make room.
      announce(STRINGS.full);
      syncButtons();
      openModal(button);
      return;
    }

    var entry = buildEntry(wrap, variantId);
    if (!entry) return;

    list.push(entry);
    write(list);
    syncAll();
    announce(entry.productTitle + ' in ' + entry.title + ' added to comparison. ' +
      list.length + ' of ' + CONFIG.maxItems + '.');

    track('add', entryPayload(entry, list));

    // Not on the first add: one color has nothing to compare against, and the modal would only
    // say to go add another. From the second on there is something to show.
    if (list.length > 1) openModal(button);
  }

  /* ------------------------------------------------------------------ TABLE */

  function renderTable() {
    if (!ui.body) return;
    ui.body.textContent = '';

    var list = read();

    if (ui.summary) {
      ui.summary.textContent = list.length
        ? list.length + (list.length === 1 ? ' color' : ' colors') + ' selected, up to ' + CONFIG.maxItems
        : '';
    }
    if (ui.tools) ui.tools.hidden = list.length === 0;

    // Lets the dialog shrink to the empty-state message instead of holding the full table width.
    if (ui.dialog) {
      ui.dialog.classList.toggle('YarnCompareModal__Dialog--empty', list.length === 0);
    }

    if (!list.length) {
      var empty = el('div', 'YarnCompare__EmptyState');
      empty.appendChild(el('h3', null, STRINGS.emptyTitle));
      empty.appendChild(el('p', null, STRINGS.emptyBody));
      ui.body.appendChild(empty);
      return;
    }

    if (ui.view) {
      var anySwatch = list.some(function (entry) { return !!entry.swatch; });
      ui.view.hidden = !anySwatch;
      if (!anySwatch && ui.viewMode === 'swatch') ui.viewMode = 'skein';
    }

    var diffOnly = ui.diff && ui.diff.checked && list.length > 1;

    var scroll = el('div', 'YarnCompare__Scroll');
    scroll.setAttribute('role', 'region');
    scroll.setAttribute('aria-label', 'Yarn comparison table, scrollable');
    scroll.setAttribute('tabindex', '0');

    var table = el('table', 'YarnCompare__Table');
    table.appendChild(el('caption', 'YarnCompare__SrOnly',
      'Comparing ' + list.length + (list.length === 1 ? ' yarn color' : ' yarn colors')));

    /* head */
    var thead = el('thead');
    var headRow = el('tr');
    headRow.appendChild(el('td', 'YarnCompare__Corner'));

    list.forEach(function (entry) {
      var th = el('th');
      th.setAttribute('scope', 'col');

      var card = el('div', 'YarnCompare__Card');

      var wantSwatch = ui.viewMode === 'swatch';
      var source = wantSwatch ? (entry.swatch || entry.image) : entry.image;
      var showingSwatch = wantSwatch && !!entry.swatch;

      var remove = el('button', 'YarnCompare__Remove');
      remove.type = 'button';
      remove.setAttribute('data-yarn-compare-remove', String(entry.id));
      remove.setAttribute('aria-label', 'Remove ' + entry.productTitle + ' in ' + entry.title + ' from comparison');
      remove.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">' +
        '<path d="M4 4 L16 16 M16 4 L4 16" stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round"/></svg>';

      if (source) {
        // The button lives inside the thumbnail wrapper, which is capped in CSS, so it stays on
        // the image rather than drifting to the far edge of a wide column.
        var thumbWrap = el('div', 'YarnCompare__ThumbWrap');
        var img = el('img', 'YarnCompare__Thumb' + (showingSwatch ? ' YarnCompare__Thumb--swatch' : ''));
        img.src = source;
        img.alt = entry.productTitle + ' in ' + entry.title +
          (showingSwatch ? ', color swatch' : '');
        img.loading = 'lazy';
        thumbWrap.appendChild(img);
        thumbWrap.appendChild(remove);
        card.appendChild(thumbWrap);
      } else {
        card.className += ' YarnCompare__Card--noImage';
        card.appendChild(remove);
      }

      // Swatch view requested but this color has none, so the skein is shown instead.
      if (wantSwatch && !entry.swatch) {
        card.appendChild(el('span', 'YarnCompare__ThumbNote', STRINGS.noSwatch));
      }

      var title = el('span', 'YarnCompare__CardTitle');
      var link = el('a', null, entry.productTitle);
      link.href = entry.url; // already locale-aware, came straight from Liquid
      title.appendChild(link);
      card.appendChild(title);

      card.appendChild(el('span', 'YarnCompare__CardColor', entry.title));

      if (entry.available === false) {
        card.appendChild(el('span', 'YarnCompare__CardFlag', STRINGS.outOfStock));
      }

      var bag = el('button', 'YarnCompare__Bag');
      bag.type = 'button';
      if (entry.available === false) {
        bag.disabled = true;
        bag.textContent = STRINGS.soldOut;
      } else {
        bag.setAttribute('data-yarn-compare-bag', String(entry.id));
        bag.textContent = STRINGS.addToBag;
        bag.setAttribute('aria-label',
          STRINGS.addToBag + ', ' + entry.productTitle + ' in ' + entry.title);
      }
      card.appendChild(bag);

      th.appendChild(card);
      headRow.appendChild(th);
    });

    // Trailing spacer column. Absorbs the leftover width of the dialog so the real columns stay
    // at their natural size instead of stretching, and carries the "add another" prompt.
    var remaining = CONFIG.maxItems - list.length;
    var spacerClass = 'YarnCompare__Spacer' + (remaining > 0 ? '' : ' YarnCompare__Spacer--collapsed');
    var spacerHead = el('td', spacerClass);
    if (remaining > 0) {
      var prompt = el('div', 'YarnCompare__Prompt');
      prompt.appendChild(el('span', 'YarnCompare__PromptTitle', STRINGS.addMoreTitle));
      prompt.appendChild(el('span', 'YarnCompare__PromptBody', STRINGS.addMoreBody));
      prompt.appendChild(el('span', 'YarnCompare__PromptCount',
        remaining === 1 ? 'Room for 1 more' : 'Room for ' + remaining + ' more'));
      spacerHead.appendChild(prompt);
    }
    headRow.appendChild(spacerHead);

    thead.appendChild(headRow);
    table.appendChild(thead);

    /* body */
    var tbody = el('tbody');

    ROWS.forEach(function (row) {
      var values = list.map(function (entry) { return comparableValue(entry, row); });
      var allBlank = values.every(function (v) { return v === ''; });
      if (allBlank) return;

      var differs = list.length > 1 && values.some(function (v) { return v !== values[0]; });
      if (diffOnly && !differs) return;

      var tr = el('tr');
      if (differs) tr.className = 'is-different';

      var label = el('th');
      label.setAttribute('scope', 'row');

      // Break before a trailing parenthetical so it wraps as a unit rather than splitting
      // across lines, e.g. "Crochet Gauge" / "(4in x 4in)".
      var parenAt = row.label.indexOf(' (');
      if (parenAt > -1) {
        label.appendChild(document.createTextNode(row.label.slice(0, parenAt)));
        label.appendChild(el('span', 'YarnCompare__RowLabelNote', row.label.slice(parenAt + 1)));
      } else {
        label.appendChild(document.createTextNode(row.label));
      }
      // Non-color cue so the highlight is not conveyed by color alone (WCAG 1.4.1).
      if (differs) label.appendChild(srOnly(' \u2013 ' + STRINGS.differs));
      tr.appendChild(label);

      list.forEach(function (entry) {
        var td = el('td');
        td.appendChild(cellContent(entry, row));
        tr.appendChild(td);
      });

      var spacerCell = el('td', spacerClass);
      spacerCell.setAttribute('aria-hidden', 'true');
      tr.appendChild(spacerCell);

      tbody.appendChild(tr);
    });

    if (!tbody.children.length) {
      var matchRow = el('tr');
      var matchCell = el('td', null, STRINGS.allMatch);
      matchCell.setAttribute('colspan', String(list.length + 2));
      matchRow.appendChild(matchCell);
      tbody.appendChild(matchRow);
    }

    table.appendChild(tbody);
    scroll.appendChild(table);
    ui.body.appendChild(scroll);
  }

  /* ------------------------------------------------------------------ ADD TO BAG */

  /* Posts to the same /cart/add.js the theme uses. Quantity is always 1: the decision in this
     modal is which color, not how many, and the cart is where quantity belongs.

     Dispatches the theme's product:added afterwards, which rerenders the cart drawer and opens
     it. The compare modal stays open underneath. */
  function addToBag(button) {
    if (button.disabled) return;

    var id = button.getAttribute('data-yarn-compare-bag');
    var list = read();
    var entry = list[indexOfId(list, id)];
    if (!entry) return;

    button.disabled = true;
    button.textContent = STRINGS.adding;

    fetch(rootUrl() + '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: entry.id, quantity: 1 })
    }).then(function (response) {
      if (!response.ok) throw new Error('add failed');
      return response.json();
    }).then(function () {
      track('add_to_bag', entryPayload(entry, list));
      announce(entry.productTitle + ' in ' + entry.title + ' added to bag.');

      button.classList.add('YarnCompare__Bag--added');
      button.textContent = STRINGS.addedToBag;
      window.setTimeout(function () {
        if (!document.body.contains(button)) return;
        button.classList.remove('YarnCompare__Bag--added');
        button.textContent = STRINGS.addToBag;
        button.disabled = false;
      }, 2500);

      // The theme's own event. It is what rerenders the drawer from the real cart -- without it
      // the drawer shows whatever it had cached and the new line is missing. It also opens the
      // drawer, which is fine: CSS lifts the drawer above this modal, so closing it returns to
      // the comparison rather than ending it.
      document.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: { id: entry.id, title: entry.title, sku: entry.sku }, quantity: 1 }
      }));
    }).catch(function () {
      button.disabled = false;
      button.textContent = STRINGS.addFailed;
      announce(STRINGS.addFailed);
      window.setTimeout(function () {
        if (document.body.contains(button)) button.textContent = STRINGS.addToBag;
      }, 3000);
    });
  }

  /* ------------------------------------------------------------------ REFRESH */

  // Re-checks price and availability, and drops entries whose product or variant no longer exists.
  function refresh() {
    if (!CONFIG.refreshOnOpen || !window.fetch) return;
    var list = read();
    if (!list.length) return;

    var handles = list.map(function (entry) { return entry.handle; })
      .filter(function (h, i, arr) { return arr.indexOf(h) === i; });

    Promise.all(handles.map(function (handle) {
      return fetch(rootUrl() + '/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (response) {
          if (response.ok) return response.json();
          // Only a 404 means the product is actually gone. Treating every non-OK status as gone
          // would wipe a shopper's comparison on a 500, a 429, or a mis-set locale root URL.
          return response.status === 404 ? null : undefined;
        })
        .catch(function () { return undefined; }); // network error: leave the entry alone
    })).then(function (results) {
      var byHandle = {};
      handles.forEach(function (handle, i) { byHandle[handle] = results[i]; });

      var current = read();
      var changed = false;

      var next = current.filter(function (entry) {
        var product = byHandle[entry.handle];
        if (product === undefined) return true;          // not fetched or request failed
        if (product === null) { changed = true; return false; } // 404, product gone

        // A response that parsed but is not a product -- an app proxy, an error page served as
        // JSON, an unexpected shape. Leave the entry alone rather than throwing: this runs inside
        // a promise, and the modal has already rendered from the stored snapshot.
        if (!product.variants || !product.variants.length) return true;

        var match = null;
        for (var i = 0; i < product.variants.length; i++) {
          if (String(product.variants[i].id) === String(entry.id)) { match = product.variants[i]; break; }
        }
        if (!match) { changed = true; return false; }

        if (entry.price !== match.price ||
            entry.compareAtPrice !== match.compare_at_price ||
            entry.available !== match.available) {
          entry.price = match.price;
          entry.compareAtPrice = match.compare_at_price;
          entry.available = match.available;
          changed = true;
        }
        return true;
      });

      if (changed) {
        write(next);
        syncAll();
      }
    }).catch(function () {
      /* Refreshing prices is best effort. The comparison is already on screen from the stored
         snapshot, so a failure here must not surface as an unhandled rejection. */
    });
  }

  /* ------------------------------------------------------------------ MODAL */

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),' +
    'textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function focusables() {
    return $$(FOCUSABLE, ui.dialog).filter(function (node) {
      return node.offsetWidth > 0 || node.offsetHeight > 0 || node === document.activeElement;
    });
  }

  function onKeydown(event) {
    if (!ui.isOpen) return;

    if (event.key === 'Escape' || event.keyCode === 27) {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== 'Tab' && event.keyCode !== 9) return;

    var items = focusables();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function backgroundNodes() {
    return $$('body > *').filter(function (node) {
      return node !== ui.root && node.nodeName !== 'SCRIPT' && node.nodeName !== 'STYLE';
    });
  }

  function openModal(trigger) {
    if (ui.isOpen || !ui.modal) return;
    ui.lastFocused = trigger || document.activeElement;
    ui.isOpen = true;

    ui.modal.hidden = false;
    if (ui.tab) ui.tab.setAttribute('aria-expanded', 'true');

    renderTable();

    // Move focus into the dialog BEFORE hiding the background, so aria-hidden never
    // covers the element that currently has focus.
    var target = $('[data-yarn-compare-close]:not(.YarnCompareModal__Overlay)', ui.dialog) || ui.dialog;
    target.focus();

    // Remember any aria-hidden the theme already set (its Modal class sets some) so closing
    // restores it instead of wiping it.
    backgroundNodes().forEach(function (node) {
      node.setAttribute('data-yarn-compare-inert',
        node.hasAttribute('aria-hidden') ? node.getAttribute('aria-hidden') : '');
      node.setAttribute('aria-hidden', 'true');
    });

    // The cart drawer and theme modals use the same no-scroll class. Only release it on close
    // if we were the ones who applied it.
    ui.wasLocked = document.documentElement.classList.contains('no-scroll');
    document.documentElement.classList.add('no-scroll');
    document.documentElement.classList.add('yarn-compare-open');

    var open_list = read();
    track('open', {
      compare_count: open_list.length,
      compare_products: open_list.map(function (e) { return e.handle; })
        .filter(function (h, i, a) { return a.indexOf(h) === i; }).length
    });

    refresh();
  }

  function canFocus(node) {
    return node &&
      document.body.contains(node) &&
      !node.disabled &&
      !node.hidden &&
      (node.offsetWidth > 0 || node.offsetHeight > 0);
  }

  function closeModal() {
    if (!ui.isOpen || !ui.modal) return;
    ui.isOpen = false;
    ui.modal.hidden = true;
    if (ui.tab) ui.tab.setAttribute('aria-expanded', 'false');

    $$('[data-yarn-compare-inert]').forEach(function (node) {
      var previous = node.getAttribute('data-yarn-compare-inert');
      node.removeAttribute('data-yarn-compare-inert');
      if (previous) {
        node.setAttribute('aria-hidden', previous);
      } else {
        node.removeAttribute('aria-hidden');
      }
    });

    document.documentElement.classList.remove('yarn-compare-open');
    if (!ui.wasLocked) document.documentElement.classList.remove('no-scroll');
    ui.wasLocked = false;

    // The trigger may have been removed or hidden since the modal opened (the tab hides at zero),
    // so fall back rather than leaving focus on the body.
    if (canFocus(ui.lastFocused)) {
      ui.lastFocused.focus();
    } else if (canFocus(ui.tab)) {
      ui.tab.focus();
    } else {
      var addButton = $('[data-yarn-compare-toggle]');
      if (canFocus(addButton)) addButton.focus();
    }
    ui.lastFocused = null;

    // Colors can be removed from inside the modal, so the PDP button may be stale.
    syncButtons();
  }

  /* ------------------------------------------------------------------ EVENTS */

  function onBodyClick(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var toggle = target.closest('[data-yarn-compare-toggle]');
    if (toggle) { handleToggle(toggle); return; }

    var opener = target.closest('[data-yarn-compare-open]');
    if (opener) { openModal(opener); return; }

    if (target.closest('[data-yarn-compare-close]')) { closeModal(); return; }

    var bagButton = target.closest('[data-yarn-compare-bag]');
    if (bagButton) { addToBag(bagButton); return; }

    var removeButton = target.closest('[data-yarn-compare-remove]');
    if (removeButton) {
      var id = removeButton.getAttribute('data-yarn-compare-remove');
      var list = read();
      var position = indexOfId(list, id);
      if (position === -1) return;
      var removed = list.splice(position, 1)[0];
      write(list);
      syncAll();
      announce(removed.productTitle + ' in ' + removed.title + ' removed. ' + list.length + ' remaining.');
      track('remove', entryPayload(removed, list));
      var next = $('[data-yarn-compare-remove]', ui.body) ||
        $('[data-yarn-compare-close]:not(.YarnCompareModal__Overlay)', ui.dialog);
      if (next) next.focus();
      return;
    }

  }

  function init() {
    ui.root = $('[data-yarn-compare-root]');
    if (!ui.root) return;

    ui.tab = $('[data-yarn-compare-open]', ui.root);
    ui.count = $('[data-yarn-compare-count]', ui.root);
    ui.tabLabel = $('[data-yarn-compare-tab-label]', ui.root);
    ui.modal = $('[data-yarn-compare-modal]', ui.root);
    ui.dialog = $('#YarnCompareDialog', ui.root);
    ui.body = $('[data-yarn-compare-body]', ui.root);
    ui.summary = $('[data-yarn-compare-summary]', ui.root);
    ui.tools = $('[data-yarn-compare-tools]', ui.root);
    ui.diff = $('[data-yarn-compare-diff]', ui.root);
    ui.view = $('[data-yarn-compare-view]', ui.root);
    ui.announce = $('[data-yarn-compare-announce]', ui.root);

    document.body.addEventListener('click', onBodyClick);

    window.addEventListener('scroll', queueDodge, { passive: true });
    window.addEventListener('resize', queueDodge);
    document.addEventListener('keydown', onKeydown);

    if (ui.diff) ui.diff.addEventListener('change', renderTable);

    ui.viewMode = readView();
    if (ui.view) {
      var initial = $('input[value="' + ui.viewMode + '"]', ui.view);
      if (initial) initial.checked = true;

      // Keyboard and native label activation.
      ui.view.addEventListener('change', function (event) {
        if (!event.target || event.target.type !== 'radio') return;
        applyView(event.target.value);
      });

      // Any click anywhere in the pill flips to the other side, including a click on the label
      // that is already active. preventDefault stops the label from activating its own radio
      // first, which would otherwise select the side that was clicked before this flips it.
      ui.view.addEventListener('click', function (event) {
        var target = event.target;
        if (!target || target.tagName === 'INPUT') return;

        event.preventDefault();

        var next = $('input:not(:checked)', ui.view);
        if (!next) return;
        next.checked = true;
        applyView(next.value);
      });
    }

    // theme.min.js dispatches variant:changed and only then writes the new id into
    // .ProductForm__VariantId, so reading it synchronously returns the previous variant.
    // A zero timeout runs after that assignment completes.
    document.addEventListener('variant:changed', deferredSync);

    // Covers swatch clicks that do not complete a variant selection, and the swatch modal.
    document.addEventListener('change', function (event) {
      if (event.target && event.target.closest && event.target.closest('.ProductForm__Option')) {
        deferredSync();
      }
    });

    // Keep other open tabs in step.
    window.addEventListener('storage', function (event) {
      if (event.key === CONFIG.storageKey) syncAll();
    });

    syncAll();

    // The swatch carousel initialises after load and may clone cells, so run once more.
    window.setTimeout(markSwatches, 600);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
